import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import Button from "@mui/material/Button";

// A sankey diagram rendered with d3, for the vintrace batch movement graph.
// The layout is a port of the POC (hacks/vintrace_graph/sankey.go),
// extended with the POC's node-crossing avoidance: nodes
// are batches, vessels without a vintrace batch, and the Intake/Packaging
// pseudo-nodes that one-sided movements connect to. They sit in columns
// (longest-path layering, computed by the backend) so every ribbon flows
// strictly left to right; ribbons are sized by the volume that pertains to
// the graph's root lot (link.portion, falling back to the full volume);
// a link that closes a cycle (back: true) loops out right of its source
// and back in left of its target, so it still leaves right and enters
// left. Such a loop can fold back over its own band where the two slots
// sit at similar heights; the ribbon is then painted as one path per
// x-monotonic window of the band, so the overlap reads darker instead of
// the fill cancelling itself out (zero winding).

// A batch's lost wine (node.loss) runs right off the bottom of the node's
// right edge like its outgoing ribbons, then curves down and fades out; a
// batch's net increase (node.gain) is the same ribbon rotated 180 degrees
// at the topleft of the node — it enters the top of the node's left edge,
// running in from the upper left and fading out.
// Node labels carry the lot mapping / ferment start for batches, the local
// id for vessels, and "Extraction" for intakes, with the job's identifier
// ("Work Order 41008", else the date) on the smaller sub line and the
// grower and block of the fruit parcels on the smallest line below that
// (node.subsub; the per-parcel detail — grower, block, docket, weight — is
// one node-tooltip line per parcel), plus the root-lot share as
// "portion/total".
// The diagram is laid out on a fixed canvas (the viewBox) and scaled to
// its container. A node can be dragged vertically to fine-tune its
// placement; its ribbons (and loss drain) follow in real time.

// One fruit parcel of an intake node (its extraction workorder's
// additionalDetails.extraction.parcels): one delivery of fruit pressed
// into the batch.
export interface SankeyParcel {
  docket: string;
  growerName?: string;
  blockName?: string;
  weight: number;
  weightUnit?: string;
}

export interface SankeyNode {
  id: number;
  kind: string; // "batch", "vessel", "intake" or "packaging"
  name: string;
  sub: string;
  // the fruit detail of an intake node (the grower and block of its
  // parcels, the first of each plus a count for several): the smallest
  // label line, below the name and the sub
  subsub?: string;
  // the fruit parcels of an intake node (its extraction workorder's
  // additionalDetails.extraction.parcels); one tooltip line per parcel
  parcels?: SankeyParcel[];
  ferm: boolean;
  layer: number;
  // the local lot this batch maps to (batch nodes only); makes the node
  // clickable so it can deep-link to that lot's own graph
  lotID?: number;
  // the share of the node's wine that pertains to the graph's root lot
  // (batch nodes only); the sub label renders them as "portion/total"
  portion?: number;
  total?: number;
  // wine lost with the node's outgoing transactions (lossDetails, batch
  // nodes only); the loss ribbon drains out of the node's right edge and
  // is sized by the share that pertains to the root lot
  loss?: number;
  lossPortion?: number;
  // wine the node's lossDetails increased it by (the "adjustment" sign
  // convention, batch nodes only); the increase ribbon enters the top of
  // the node's left edge and is sized by the share that pertains to the
  // root lot
  gain?: number;
  gainPortion?: number;
  // the node fell below the dead-end threshold (a small share of the
  // root lot's volume, computed server-side): rendered dimmed, and the
  // walk stops there (nothing beyond it is rendered)
  dim?: boolean;
}

export interface SankeyLink {
  source: number;
  target: number;
  volume: number;
  // the share of the movement that pertains to the graph's root lot;
  // the ribbon is sized by this, falling back to the full volume
  portion?: number;
  n: number;
  minDate?: string;
  maxDate?: string;
  back?: boolean;
}

export interface SankeyProps {
  nodes: SankeyNode[];
  links: SankeyLink[];
  title: string;
  subtitle?: string;
  footer?: string;
  unit?: string;
  // when set, an Export button is shown in the top right; it downloads
  // the rendered diagram as `${exportName}.svg`
  exportName?: string;
  // the id of the node whose name label is rendered bold (the graph's
  // root lot); omitted when there is no node to highlight
  highlightID?: number;
  // the name of the graph's root lot, used in the node tooltip
  // ("... pertains to <name>"); falls back to "this lot" when omitted
  rootLotName?: string;
  // called when a node that maps to a local lot (node.lotID) is clicked;
  // lets the caller deep-link to that lot's own graph
  onNodeClick?: (node: SankeyNode) => void;
}

// Layout constants (tuned sparser than the POC, so ribbons and
// unrelated nodes get room instead of overlapping)
const nodeW = 12; // node width in px
const colGap = 220; // horizontal room between columns (back-edge clearance)
const rowGap = 50; // vertical gap between nodes in a column
const minH = 14; // minimum node height in px
const minW = 2; // minimum ribbon width in px
const sideMargin = 4; // clearance a ribbon keeps around an unrelated node's top/bottom/left
const flatEps = 0.5; // ribbon ends differing by less than this make a "perfectly horizontal" ribbon
const flatNudge = 4; // the vertical shift avoidFlatBundles uses to un-align a horizontal bundle
const topPad = 104; // room for the title, subtitle and legend
const bottomPad = 56; // room for the footer
// free space the svg always keeps below the laid-out diagram: room to
// drag a node down (or its loss drain to fall) without hitting the edge
export const bottomRoom = 200;
export const ribbonOpacity = 0.2; // ribbon fill opacity
export const ribbonOpacityHover = 0.5; // slightly stronger while hovered
// a dimmed node (below the dead-end threshold) renders faded, along with
// its ribbons and loss drain
export const dimNodeOpacity = 0.35;
export const dimRibbonOpacity = ribbonOpacity * 0.4;

// fresh id per rendered svg for the loss drain's fade gradient
let lossFadeSeq = 0;

export interface LNode {
  node: SankeyNode;
  x: number;
  y: number;
  w: number;
  h: number;
  labelY: number; // vertical centre of the node's label (de-overlapped)
  in: number;
  out: number; // flow through the node
  lossFlow: number; // volume the loss drain is sized by (portion, else full)
  lossW: number;
  lossY0: number;
  lossY1: number; // the loss drain's slot on the node's right edge
  gainFlow: number; // volume the increase ribbon is sized by (portion, else full)
  gainW: number;
  gainY0: number;
  gainY1: number; // the increase ribbon's slot on the node's left edge
}

export interface LLink {
  s: LNode;
  t: LNode;
  volume: number; // full movement volume (tooltips)
  portion?: number; // share that pertains to the root lot (tooltips)
  flow: number; // volume the ribbon is sized by: portion, or the full volume
  n: number;
  minDate?: string;
  maxDate?: string;
  back: boolean;
  width: number;
  sy0: number;
  sy1: number; // ribbon top/bottom on the source's right edge
  ty0: number;
  ty1: number; // ribbon top/bottom on the target's left edge
}

// One line of the custom hover tooltip (rendered as formatted HTML
// instead of the browser-native <title> tooltip).
interface TipLine {
  text: string;
  strong?: boolean;
  muted?: boolean;
}

interface SankeyTip {
  x: number;
  y: number;
  lines: TipLine[];
}

export interface SankeyLayout {
  width: number;
  height: number;
  nodes: LNode[];
  links: LLink[];
}

const flowOf = (n: LNode) => Math.max(n.in, n.out);

// The node's sub label, with the root-lot share appended for nodes that
// carry one ("... · 500/10000"); used for the label width budget and the
// rendered text.
const nodeSubLabel = (n: SankeyNode) => {
  if (n.portion == null || n.total == null) {
    return n.sub;
  }
  const share = `${Math.round(n.portion)}/${Math.round(n.total)}`;
  return n.sub ? `${n.sub} · ${share}` : share;
};

// The node's label width budget (~8px per char at the 13px name size,
// ~6.5px at the 10px sub-label size); used for the canvas width and the
// label part of the node's effective area.
const labelWidth = (n: SankeyNode) =>
  Math.max(
    8 * n.name.length,
    6.5 * nodeSubLabel(n).length,
    6.5 * (n.subsub || "").length,
  );

// reduceCrossings reorders the nodes within each column to reduce ribbon
// crossings: a few barycenter passes over the columns, each node sorting
// toward the average rank of the nodes it connects to in the adjacent
// columns. Nodes without such neighbours keep their flow-descending rank.
const reduceCrossings = (
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
) => {
  for (const c of colIDs) {
    const col = cols.get(c)!;
    col.sort((a, b) => {
      if (flowOf(a) !== flowOf(b)) {
        return flowOf(b) - flowOf(a);
      }
      return a.node.name.localeCompare(b.node.name);
    });
  }
  const rank = (c: number) => {
    const m = new Map<LNode, number>();
    cols.get(c)!.forEach((n, i) => m.set(n, i));
    return m;
  };
  for (let pass = 0; pass < 4; pass++) {
    for (let i = 0; i < colIDs.length; i++) {
      const ci = pass % 2 === 1 ? colIDs.length - 1 - i : i;
      const c = colIDs[ci];
      const left = ci > 0 ? rank(colIDs[ci - 1]) : undefined;
      const right = ci < colIDs.length - 1 ? rank(colIDs[ci + 1]) : undefined;
      const slots: { n: LNode; pos: number }[] = [];
      cols.get(c)!.forEach((n, j) => {
        let sum = 0;
        let cnt = 0;
        for (const l of links) {
          let nb: LNode | undefined;
          let r: Map<LNode, number> | undefined;
          if (l.s === n) {
            nb = l.t;
            r = right;
          } else if (l.t === n) {
            nb = l.s;
            r = left;
          } else {
            continue;
          }
          if (nb && r && r.has(nb)) {
            sum += r.get(nb)!;
            cnt++;
          }
        }
        slots.push({ n, pos: cnt > 0 ? sum / cnt : j });
      });
      slots.sort((a, b) => a.pos - b.pos);
      slots.forEach((s, j) => (cols.get(c)![j] = s.n));
    }
  }
};

// assignAnchors stacks each node's outgoing ribbons (right edge) and
// incoming ribbons (left edge) in the other node's vertical order, sizing
// them by volume. The batch's loss drain is pinned to the bottom of the
// right edge while the real ribbons stack from the top (sized and squeezed
// together with the loss, so the whole right edge fits the node; when the
// outflows don't fill the node, the gap sits between them), so the drain
// can fall straight down clear of them; the increase ribbon takes the top
// slot of the left edge, so it can rise straight up clear of them. The
// anchors depend on the node positions, so this runs once per relaxation
// sweep and once more for the final positions.
const assignAnchors = (nodes: LNode[], links: LLink[], scale: number) => {
  const outLinks = new Map<LNode, LLink[]>();
  const inLinks = new Map<LNode, LLink[]>();
  for (const l of links) {
    const out = outLinks.get(l.s);
    if (out) out.push(l);
    else outLinks.set(l.s, [l]);
    const inc = inLinks.get(l.t);
    if (inc) inc.push(l);
    else inLinks.set(l.t, [l]);
  }
  const assignIn = (n: LNode, ls: LLink[]) => {
    // incoming: the real ribbons in the source's vertical order, with the
    // increase ribbon in the top slot of the stack; sized and squeezed
    // together, so the whole left edge fits the node
    ls.sort((a, b) => {
      if (a.s.y !== b.s.y) {
        return a.s.y - b.s.y;
      }
      return a.s.node.name.localeCompare(b.s.node.name);
    });
    const slots: { l: LLink | null; flow: number; w: number }[] =
      ls.map((l) => ({ l, flow: l.flow, w: 0 }));
    if (n.gainFlow > 0) {
      slots.unshift({ l: null, flow: n.gainFlow, w: 0 });
    }
    let sum = 0;
    for (const s of slots) {
      let w = s.flow * scale;
      if (w < minW) {
        w = minW;
      }
      s.w = w;
      sum += w;
    }
    if (sum > n.h && sum > 0) {
      // ribbons don't fit under the node; squeeze
      for (const s of slots) {
        s.w *= n.h / sum;
      }
    }
    let y = n.y;
    for (const s of slots) {
      if (s.l) {
        s.l.width = s.w;
        s.l.ty0 = y;
        s.l.ty1 = y + s.w;
      } else {
        n.gainW = s.w;
        n.gainY0 = y;
        n.gainY1 = y + s.w;
      }
      y += s.w;
    }
  };
  for (const n of nodes) {
    // outgoing: the real ribbons in the target's vertical order, stacked
    // from the top of the right edge, with the loss drain pinned to the
    // node's bottom; sized and squeezed together, so the whole right edge
    // fits the node (the gap, when the outflows don't fill it, sits
    // between the two)
    const ls = (outLinks.get(n) ?? []).sort((a, b) => {
      if (a.t.y !== b.t.y) {
        return a.t.y - b.t.y;
      }
      return a.t.node.name.localeCompare(b.t.node.name);
    });
    const slots: { l: LLink | null; flow: number; w: number }[] = ls.map(
      (l) => ({ l, flow: l.flow, w: 0 }),
    );
    if (n.lossFlow > 0) {
      slots.push({ l: null, flow: n.lossFlow, w: 0 });
    }
    let sum = 0;
    for (const s of slots) {
      let w = s.flow * scale;
      if (w < minW) {
        w = minW;
      }
      s.w = w;
      sum += w;
    }
    if (sum > n.h && sum > 0) {
      for (const s of slots) {
        s.w *= n.h / sum;
      }
    }
    let y = n.y;
    for (const s of slots) {
      if (s.l) {
        s.l.width = s.w;
        s.l.sy0 = y;
        s.l.sy1 = y + s.w;
        y += s.w;
      } else {
        // pinned to the bottom of the node's right edge (the squeezed sum
        // fits the node, so the slot never overlaps the ribbons)
        n.lossW = s.w;
        n.lossY0 = n.y + n.h - s.w;
        n.lossY1 = n.y + n.h;
      }
    }
    assignIn(n, inLinks.get(n) ?? []);
  }
};

// shiftNodeVertical moves a node to a new vertical position (clamped so it
// stays within [top, bottom]) and shifts every ribbon anchor on its edges,
// its loss slot and its label with it. Returns the applied delta (0 when the
// node did not move). Mutates in place — this is the data half of the drag,
// separated from the DOM updates so it can be tested on its own.
export const shiftNodeVertical = (
  n: LNode,
  links: LLink[],
  y: number,
  top: number,
  bottom: number,
): number => {
  const clamped = Math.max(top, Math.min(bottom - n.h, y));
  const dy = clamped - n.y;
  if (dy === 0) {
    return 0;
  }
  n.y = clamped;
  n.labelY += dy;
  if (n.lossFlow > 0) {
    n.lossY0 += dy;
    n.lossY1 += dy;
  }
  if (n.gainFlow > 0) {
    n.gainY0 += dy;
    n.gainY1 += dy;
  }
  for (const l of links) {
    if (l.s === n) {
      l.sy0 += dy;
      l.sy1 += dy;
    } else if (l.t === n) {
      l.ty0 += dy;
      l.ty1 += dy;
    }
  }
  return dy;
};

// relaxColumns refines the initial vertical placement (each column packed
// and centered as a block). Alternating sweeps align the flow: a
// left-to-right sweep pulls each node toward the slots its incoming
// ribbons leave at, a right-to-left sweep toward the slots its outgoing
// ribbons arrive at (volume-weighted). Thick ribbons end up running in
// their own parallel bands instead of stacked on one line. A collision
// pass keeps the column's order, row gaps and canvas bounds.
const relaxColumns = (
  nodes: LNode[],
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
  scale: number,
  top: number,
  bottom: number,
) => {
  const inLinks = new Map<LNode, LLink[]>();
  const outLinks = new Map<LNode, LLink[]>();
  for (const l of links) {
    const out = outLinks.get(l.s);
    if (out) out.push(l);
    else outLinks.set(l.s, [l]);
    const inc = inLinks.get(l.t);
    if (inc) inc.push(l);
    else inLinks.set(l.t, [l]);
  }
  for (let i = 0; i < 40; i++) {
    assignAnchors(nodes, links, scale); // anchors of the current positions
    const leftToRight = i % 2 === 0;
    let moved = 0;
    for (let ci = 0; ci < colIDs.length; ci++) {
      const c = leftToRight ? colIDs[ci] : colIDs[colIDs.length - 1 - ci];
      const col = cols.get(c)!;
      // desired shift per node: the volume-weighted mean of how far the
      // sweep-side ribbon slots are from the node's own slots
      const shift = new Array(col.length).fill(0);
      for (let j = 0; j < col.length; j++) {
        const n = col[j];
        const ls = (leftToRight ? inLinks : outLinks).get(n) ?? [];
        let sum = 0;
        let wsum = 0;
        for (const l of ls) {
          let a: number;
          let b: number;
          if (leftToRight) {
            a = (l.sy0 + l.sy1) / 2;
            b = (l.ty0 + l.ty1) / 2;
          } else {
            a = (l.ty0 + l.ty1) / 2;
            b = (l.sy0 + l.sy1) / 2;
          }
          sum += (a - b) * l.flow;
          wsum += l.flow;
        }
        if (wsum > 0) {
          shift[j] = sum / wsum;
        }
      }
      // apply, keeping the column's order and row gaps
      let y = 0;
      for (let j = 0; j < col.length; j++) {
        const n = col[j];
        y = n.y + shift[j];
        if (y < top) {
          y = top;
        }
        if (j > 0 && y < col[j - 1].y + col[j - 1].h + rowGap) {
          y = col[j - 1].y + col[j - 1].h + rowGap;
        }
        const d = Math.abs(y - n.y);
        if (d > moved) {
          moved = d;
        }
        n.y = y;
      }
      const first = col[0].y;
      const last = col[col.length - 1].y + col[col.length - 1].h;
      if (last - first > bottom - top) {
        // the desired centres span more than the canvas; pack the column
        // with its minimum span, centered
        let total = 0;
        for (const n of col) {
          total += n.h;
        }
        total += rowGap * (col.length - 1);
        y = top + (bottom - top - total) / 2;
        for (const n of col) {
          n.y = y;
          y += n.h + rowGap;
        }
        moved = 1;
      } else {
        // the span fits: slide the stack into the canvas
        if (last - bottom > 0) {
          for (const n of col) {
            n.y -= last - bottom;
          }
        }
        if (top - col[0].y > 0) {
          for (const n of col) {
            n.y += top - col[0].y;
          }
        }
      }
    }
    if (moved < 0.1) {
      break; // settled
    }
  }
  assignAnchors(nodes, links, scale); // anchors of the final positions
};

// cubicBezier evaluates one axis of a cubic Bezier at parameter t.
const cubicBezier = (
  t: number,
  p0: number,
  c0: number,
  c1: number,
  p1: number,
) => {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * c0 + 3 * u * t * t * c1 + t * t * t * p1;
};

// ribbonControl returns the x endpoints and Bezier control points of the
// link's ribbon (shared by the path rendering and the crossing checks).
// Invariant for every ribbon: c0 > x0 and c1 < x1 — the curve leaves the
// source's right edge pointing right and arrives at the target's left edge
// coming from the left (pointing right). Forward ribbons use the midpoint
// controls; a back edge (x1 <= x0, closing a cycle) bulges out right of the
// source and dips in left of the target so the loop keeps both directions.
export const ribbonControl = (l: LLink) => {
  const x0 = l.s.x + l.s.w;
  const x1 = l.t.x;
  if (x1 > x0) {
    const mid = x0 + (x1 - x0) / 2;
    return { x0, x1, c0: mid, c1: mid };
  }
  // how far the loop pokes out past its source before folding over: ~2x
  // the old reach (40 + 0.3d), which only cleared the source by a hair
  // and made the fold look cramped
  const bulge = 64 + (x0 - x1) * 0.48;
  return { x0, x1, c0: x0 + bulge, c1: x1 - bulge };
};

// tAtX finds the parameter t where the Bezier x coordinate is x (bisection;
// x(t) is monotonic for forward ribbons, whose control x's are monotonic).
const tAtX = (x: number, x0: number, c0: number, c1: number, x1: number) => {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (cubicBezier(mid, x0, c0, c1, x1) < x) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
};

// The node's effective area that a ribbon must not run over: the node body
// with a small margin on its top/bottom/left sides (the label text reaches
// a few px past the body on short nodes) plus the label zone to its right.
// Nodes and labels are painted over the ribbons, so a ribbon running
// through that area reads as crossing the node or obscuring its label.
const nodeZone = (n: LNode) => ({
  xa: n.x - sideMargin,
  xb: n.x + n.w + 7 + labelWidth(n.node),
  ya: Math.min(n.y - sideMargin, n.y + n.h / 2 - 14),
  yb: Math.max(n.y + n.h + sideMargin, n.y + n.h / 2 + 13),
});

// ribbonBandAtNode returns the vertical extent of the ribbon inside the
// node's effective area. ok is false when the ribbon never reaches it.
const ribbonBandAtNode = (l: LLink, n: LNode) => {
  const { x0, x1, c0, c1 } = ribbonControl(l);
  const z = nodeZone(n);
  let xa = z.xa;
  let xb = z.xb;
  if (x1 > x0) {
    // forward ribbon: x(t) and the band's y are both monotonic, so the
    // band's extent over the window (the area clipped to the ribbon's own
    // x range) is exactly at the window's edges
    if (x0 > xa) {
      xa = x0;
    }
    if (x1 < xb) {
      xb = x1;
    }
    if (xa >= xb) {
      return { top: 0, bot: 0, ok: false };
    }
    let top = 1e9;
    let bot = -1e9;
    for (const x of [xa, xb]) {
      const t = tAtX(x, x0, c0, c1, x1);
      for (const y of [
        cubicBezier(t, l.sy0, l.sy0, l.ty0, l.ty0),
        cubicBezier(t, l.sy1, l.sy1, l.ty1, l.ty1),
      ]) {
        if (y < top) top = y;
        if (y > bot) bot = y;
      }
    }
    return { top, bot, ok: true };
  }
  // bulge (a cycle-closing back edge): x(t) is not monotonic, sample
  let top = 1e9;
  let bot = -1e9;
  let ok = false;
  for (let i = 1; i < 96; i++) {
    const t = i / 96;
    const x = cubicBezier(t, x0, c0, c1, x1);
    if (x <= xa || x >= xb) {
      continue;
    }
    for (const y of [
      cubicBezier(t, l.sy0, l.sy0, l.ty0, l.ty0),
      cubicBezier(t, l.sy1, l.sy1, l.ty1, l.ty1),
    ]) {
      if (y < top) top = y;
      if (y > bot) bot = y;
    }
    ok = true;
  }
  return { top, bot, ok };
};

interface YInterval {
  lo: number;
  hi: number;
}

// cleanWindows returns the sub-intervals of [lo, hi] not covered by any
// blocked interval.
const cleanWindows = (lo: number, hi: number, blocked: YInterval[]) => {
  const sorted = [...blocked].sort((a, b) => a.lo - b.lo);
  const wins: YInterval[] = [];
  let cur = lo;
  for (const b of sorted) {
    if (b.hi <= cur) {
      continue;
    }
    if (b.lo > cur) {
      wins.push({ lo: cur, hi: b.lo });
    }
    if (b.hi > cur) {
      cur = b.hi;
    }
    if (cur >= hi) {
      break;
    }
  }
  if (cur < hi) {
    wins.push({ lo: cur, hi });
  }
  return wins;
};

// nearestClean returns the point of the clean windows nearest to des: des
// itself when it is already clean, and des unchanged when no window at or
// above minY qualifies (best effort).
const nearestClean = (des: number, minY: number, wins: YInterval[]) => {
  let best = des;
  let bestD = 1e18;
  for (const w of wins) {
    if (des >= w.lo && des <= w.hi) {
      return des;
    }
    let cand = w.hi;
    if (des < w.lo) {
      cand = w.lo;
    }
    if (cand < minY) {
      continue;
    }
    const d = Math.abs(cand - des);
    if (d < bestD) {
      bestD = d;
      best = cand;
    }
  }
  return best;
};

// countCrossings counts the (ribbon, node) pairs whose effective areas
// overlap — the check placeColumns drives to zero, and layoutSankey uses
// it to decide whether the scale has to shrink to give the layout room.
const countCrossings = (
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
) => {
  let n = 0;
  for (const l of links) {
    for (const c of colIDs) {
      for (const nd of cols.get(c)!) {
        if (nd === l.s || nd === l.t) {
          continue;
        }
        const b = ribbonBandAtNode(l, nd);
        if (!b.ok) {
          continue;
        }
        if (
          Math.min(b.bot, nd.y + nd.h + sideMargin) -
            Math.max(b.top, nd.y - sideMargin) >
          0.5
        ) {
          n++;
        }
      }
    }
  }
  return n;
};

// bandEdge is one x edge of the zone window as a·Y + c lines for the
// band's top and bottom curves. The band is linear in the block top Y:
// an end in the moving column travels with it (a > 0), the other end stays.
interface BandEdge {
  a: number;
  cTop: number;
  cBot: number;
}

// pairForb is the forbidden block-top range of one (ribbon, node) pair,
// plus the data needed to price a partial overlap.
interface PairForb {
  lo: number;
  hi: number;
  edges: BandEdge[];
  zt: number; // the node's zone top, relative to the block top
  zb: number; // the node's zone bottom, relative to the block top
}

// placeColumns moves each column, as a block, out of the way of ribbons
// that would visibly cross it: a ribbon's band may not pass over a node's
// effective area (a ribbon's own ends are exempt). Moving the column as a
// block keeps it compact, which keeps the fan of long ribbons it carries
// narrow. Per column, each (ribbon, node) pair the ribbon is not an end of
// forbids a range of block tops (the band is linear in the block top, so
// the range is exact). If a fully clean block top exists the nearest one
// is taken; otherwise the block slides to the top with the least overlap,
// so the neighbouring columns get room. Columns are swept left-right and
// right-left, re-anchoring after each column, until nothing moves.
const placeColumns = (
  nodes: LNode[],
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
  scale: number,
  top: number,
  bottom: number,
) => {
  for (let pass = 0; pass < 40; pass++) {
    const leftToRight = pass % 2 === 0;
    let moved = 0;
    for (let ci = 0; ci < colIDs.length; ci++) {
      const c = leftToRight ? colIDs[ci] : colIDs[colIDs.length - 1 - ci];
      const col = cols.get(c)!;
      const base = col[0].y;
      const colX = col[0].x;
      const span = col[col.length - 1].y + col[col.length - 1].h - base;
      // the column's label zone: the widest label, so the forbidden ranges
      // are shared by every node in the column
      const zoneXb =
        colX + nodeW + 7 + Math.max(...col.map((n) => labelWidth(n.node)));
      // forbidden block-top ranges, one per (ribbon, node) pair the
      // ribbon is not an end of
      const pairs: PairForb[] = [];
      for (const l of links) {
        let inS = false;
        let inT = false;
        for (const n of col) {
          if (n === l.s) {
            inS = true;
          }
          if (n === l.t) {
            inT = true;
          }
        }
        const { x0, x1, c0, c1 } = ribbonControl(l);
        const edges: BandEdge[] = [];
        if (x1 > x0) {
          let xa = colX - sideMargin;
          let xb = zoneXb;
          if (x0 > xa) {
            xa = x0;
          }
          if (x1 < xb) {
            xb = x1;
          }
          for (const xe of [xa, xb]) {
            if (xe <= x0 || xe >= x1) {
              continue; // the window edge is the ribbon's own end
            }
            const t = tAtX(xe, x0, c0, c1, x1);
            const A = (1 - t) * (1 - t) * (1 + 2 * t);
            const B = t * t * (3 - 2 * t);
            let e: BandEdge;
            if (inS) {
              e = {
                a: A,
                cTop: A * (l.sy0 - base) + B * l.ty0,
                cBot: A * (l.sy1 - base) + B * l.ty1,
              };
            } else if (inT) {
              e = {
                a: B,
                cTop: B * (l.ty0 - base) + A * l.sy0,
                cBot: B * (l.ty1 - base) + A * l.sy1,
              };
            } else {
              e = { a: 0, cTop: A * l.sy0 + B * l.ty0, cBot: A * l.sy1 + B * l.ty1 };
            }
            if (e.a < 1 - 1e-6) {
              // a window edge at the ribbon's own end travels with the
              // block (a == 1): its band part is the column's own slot and
              // never reaches another node
              edges.push(e);
            }
          }
        } else {
          // bulge (a cycle-closing back edge): sample the band over the
          // zone at the current anchors, treated as static
          const b = ribbonBandAtNode(l, col[0]);
          if (b.ok) {
            edges.push({ a: 0, cTop: b.top, cBot: b.bot });
          }
        }
        if (edges.length === 0) {
          continue;
        }
        for (const k of col) {
          if (k === l.s || k === l.t) {
            continue;
          }
          // the pair overlaps where the band's y range meets the node's
          // zone: with band = a·Y + c and the zone at [Y+zt, Y+zb], that
          // is the Y range ((cTop-zb)/(1-a), (cBot-zt)/(1-a))
          const z = nodeZone(k);
          const p: PairForb = {
            lo: 1e9,
            hi: -1e9,
            edges,
            zt: z.ya - base,
            zb: z.yb - base,
          };
          for (const e of edges) {
            const lt = (e.cTop - p.zb) / (1 - e.a);
            if (lt < p.lo) {
              p.lo = lt;
            }
            const ht = (e.cBot - p.zt) / (1 - e.a);
            if (ht > p.hi) {
              p.hi = ht;
            }
          }
          if (p.hi > p.lo) {
            pairs.push(p);
          }
        }
      }
      // place the block: the nearest clean top, else the least overlapped
      const lo = top;
      const hi = bottom - span;
      const wins = cleanWindows(
        lo,
        hi,
        pairs.map((p) => ({ lo: p.lo, hi: p.hi })),
      );
      const clean = (y: number) => wins.some((w) => y >= w.lo && y <= w.hi);
      const near = nearestClean(base, lo, wins);
      let y = base;
      if (!clean(base) && clean(near)) {
        y = near;
      } else if (!clean(base)) {
        // no clean top: the least total overlap (the overlap is piecewise
        // linear in the top, so its minimum is at a candidate)
        const cands = [lo, hi];
        for (const p of pairs) {
          if (p.lo > lo && p.lo < hi) {
            cands.push(p.lo);
          }
          if (p.hi > lo && p.hi < hi) {
            cands.push(p.hi);
          }
        }
        const price = (yy: number) => {
          let sum = 0;
          for (const p of pairs) {
            let t = 1e9;
            let b = -1e9;
            for (const e of p.edges) {
              const vt = e.a * yy + e.cTop;
              if (vt < t) {
                t = vt;
              }
              const vb = e.a * yy + e.cBot;
              if (vb > b) {
                b = vb;
              }
            }
            const ov =
              Math.min(b, yy + p.zb) - Math.max(t, yy + p.zt);
            if (ov > 0) {
              sum += ov;
            }
          }
          return sum;
        };
        let bestY = lo;
        let bestP = 1e18;
        let bestD = 1e18;
        for (const cand of cands) {
          const pc = price(cand);
          const d = Math.abs(cand - base);
          if (pc < bestP - 1e-9 || (pc <= bestP + 1e-9 && d < bestD - 1e-9)) {
            bestY = cand;
            bestP = pc;
            bestD = d;
          }
        }
        y = bestY;
      }
      for (const n of col) {
        n.y += y - base;
      }
      if (Math.abs(y - base) > moved) {
        moved = Math.abs(y - base);
      }
      assignAnchors(nodes, links, scale);
    }
    if (moved < 0.1) {
      break; // settled
    }
  }
  assignAnchors(nodes, links, scale); // anchors of the final positions
};

// countFlatBundles counts the (node, side) pairs with two or more
// perfectly horizontal ribbons on the same side: adjacent parallel bands
// of the same fill read as a single ribbon (needs current anchors).
const countFlatBundles = (nodes: LNode[], links: LLink[]) => {
  // only geometrically forward ribbons run straight; a back edge bulges
  const flat = (l: LLink) =>
    l.t.x > l.s.x + l.s.w && Math.abs(l.sy0 - l.ty0) < flatEps;
  const outL = new Map<LNode, LLink[]>();
  const inL = new Map<LNode, LLink[]>();
  for (const l of links) {
    const out = outL.get(l.s);
    if (out) out.push(l);
    else outL.set(l.s, [l]);
    const inc = inL.get(l.t);
    if (inc) inc.push(l);
    else inL.set(l.t, [l]);
  }
  let n = 0;
  for (const nd of nodes) {
    let fo = 0;
    for (const l of outL.get(nd) ?? []) {
      if (flat(l)) fo++;
    }
    let fi = 0;
    for (const l of inL.get(nd) ?? []) {
      if (flat(l)) fi++;
    }
    if (fo >= 2) n++;
    if (fi >= 2) n++;
  }
  return n;
};

// avoidFlatBundles breaks bundles of perfectly horizontal ribbons: when
// two or more of a node's ribbons on one side run exactly level with their
// far ends, they read as a single ribbon. The other end of every flat
// ribbon but the top one is moved further and further away down the stack
// (an equal move of all of them would only tilt the whole fan, keeping it
// merged), so each opens a gap to the ribbon above it at the far end. The
// moves keep each column's order and row gaps; the attempt loop prices the
// result, so a nudge that creates a new crossing is never kept over a
// cleaner layout.
const avoidFlatBundles = (
  nodes: LNode[],
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
  scale: number,
  top: number,
  bottom: number,
) => {
  const flat = (l: LLink) =>
    l.t.x > l.s.x + l.s.w && Math.abs(l.sy0 - l.ty0) < flatEps;
  // the node's movable range within its column (order and row gaps kept)
  const rangeOf = (n: LNode): [number, number] => {
    const col = cols.get(n.node.layer)!;
    const i = col.indexOf(n);
    let lo = top;
    if (i > 0) {
      lo = Math.max(lo, col[i - 1].y + col[i - 1].h + rowGap);
    }
    let hi = bottom - n.h;
    if (i < col.length - 1) {
      hi = Math.min(hi, col[i + 1].y - rowGap - n.h);
    }
    return [lo, hi];
  };
  for (let pass = 0; pass < 40; pass++) {
    assignAnchors(nodes, links, scale);
    let fix: LNode | null = null;
    let fixIn = false;
    let flatList: LLink[] = [];
    outer: for (const c of colIDs) {
      for (const n of cols.get(c)!) {
        const fo: LLink[] = [];
        const fi: LLink[] = [];
        for (const l of links) {
          if (!flat(l)) {
            continue;
          }
          if (l.s === n) {
            fo.push(l);
          } else if (l.t === n) {
            fi.push(l);
          }
        }
        if (fo.length >= 2) {
          // stack order: the anchors were assigned sorted by the other
          // node's y, which is this order for flat ribbons
          fo.sort(
            (a, b) =>
              a.t.y - b.t.y || a.t.node.name.localeCompare(b.t.node.name),
          );
          fix = n;
          fixIn = false;
          flatList = fo;
          break outer;
        }
        if (fi.length >= 2) {
          fi.sort(
            (a, b) =>
              a.s.y - b.s.y || a.s.node.name.localeCompare(b.s.node.name),
          );
          fix = n;
          fixIn = true;
          flatList = fi;
          break outer;
        }
      }
    }
    if (!fix) {
      break;
    }
    let moved = false;
    for (const dir of [1, -1]) {
      const otherOf = (l: LLink) => (fixIn ? l.s : l.t);
      const fits = flatList.every((l, i) => {
        if (i === 0) {
          return true; // the topmost ribbon stays horizontal
        }
        const o = otherOf(l);
        const [lo, hi] = rangeOf(o);
        const y = o.y + dir * i * flatNudge;
        return y >= lo - 1e-9 && y <= hi + 1e-9;
      });
      if (fits) {
        for (let i = 1; i < flatList.length; i++) {
          otherOf(flatList[i]).y += dir * i * flatNudge;
        }
        moved = true;
        break;
      }
    }
    if (!moved) {
      break; // no room to un-align this node
    }
  }
  assignAnchors(nodes, links, scale);
  return countFlatBundles(nodes, links);
};

// splitCubic returns the control points (p0, c0, c1, p1) of the cubic
// bezier restricted to t in [t0, t1] (de Casteljau) — the exact sub-curve.
const splitCubic = (
  t0: number,
  t1: number,
  p0: number,
  c0: number,
  c1: number,
  p1: number,
): [number, number, number, number] => {
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  // cut at t0: keeps [p0, a0, b0, mid] and the right part
  // [mid, b1, a2, p1] over [t0, 1]
  const a0 = lerp(p0, c0, t0);
  const a1 = lerp(c0, c1, t0);
  const a2 = lerp(c1, p1, t0);
  const b0 = lerp(a0, a1, t0);
  const b1 = lerp(a1, a2, t0);
  const mid = lerp(b0, b1, t0);
  // cut the right part at (t1 - t0) / (1 - t0): its left is [t0, t1]
  const v = (t1 - t0) / (1 - t0);
  const r0 = lerp(mid, b1, v);
  const r1 = lerp(b1, a2, v);
  const r2 = lerp(a2, p1, v);
  const s0 = lerp(r0, r1, v);
  const s1 = lerp(r1, r2, v);
  return [mid, r0, s0, lerp(s0, s1, v)];
};

// ribbonXBreaks returns the parameters in (0, 1) where the ribbon's x
// curve X(t) has a local extremum — the roots of its derivative, a
// quadratic in t. X is monotonic between consecutive breaks, so a band
// window cut there cannot overlap itself in x.
export const ribbonXBreaks = (
  x0: number,
  c0: number,
  c1: number,
  x1: number,
): number[] => {
  // X'(t) / 3 = A t^2 + B t + C
  const A = x1 - x0 + 3 * (c0 - c1);
  const B = 2 * (x0 - 2 * c0 + c1);
  const C = c0 - x0;
  const breaks: number[] = [];
  const push = (t: number) => {
    if (t > 1e-3 && t < 1 - 1e-3) {
      breaks.push(t);
    }
  };
  if (Math.abs(A) < 1e-12) {
    if (Math.abs(B) > 1e-12) {
      push(-C / B);
    }
  } else {
    const disc = B * B - 4 * A * C;
    if (disc > 0) {
      const sq = Math.sqrt(disc);
      push((-B - sq) / (2 * A));
      push((-B + sq) / (2 * A));
    }
  }
  breaks.sort((a, b) => a - b);
  return breaks;
};

// The link's ribbon band as filled path segments, one per x-monotonic
// window (the windows of ribbonXBreaks). A cycle-closing back edge whose
// slots sit at similar heights folds its loop back over its own band; as a
// single filled path that overlap carries a zero winding number, so the
// fill cancels it out and the ribbon visually falls apart. Painting each
// window with its own opacity instead keeps every pass visible: where two
// windows of the same ribbon overlap the color is painted twice and reads
// darker. A forward ribbon — and a back edge whose loop does not fold —
// is a single window, i.e. exactly the classic band path.
export const ribbonSegments = (l: LLink): string[] => {
  const { x0, x1, c0, c1 } = ribbonControl(l);
  const ts = [0, ...ribbonXBreaks(x0, c0, c1, x1), 1];
  const f = (v: number) => v.toFixed(1);
  const segs: string[] = [];
  for (let i = 0; i + 1 < ts.length; i++) {
    // the band's edges as cubics over the window: x, the top edge (both
    // controls at sy0, then both at ty0) and the bottom edge likewise
    const sx = splitCubic(ts[i], ts[i + 1], x0, c0, c1, x1);
    const syT = splitCubic(ts[i], ts[i + 1], l.sy0, l.sy0, l.ty0, l.ty0);
    const syB = splitCubic(ts[i], ts[i + 1], l.sy1, l.sy1, l.ty1, l.ty1);
    segs.push(
      `M${f(sx[0])} ${f(syT[0])}` +
        ` C${f(sx[1])} ${f(syT[1])} ${f(sx[2])} ${f(syT[2])} ${f(sx[3])} ${f(syT[3])}` +
        ` L${f(sx[3])} ${f(syB[3])}` +
        ` C${f(sx[2])} ${f(syB[2])} ${f(sx[1])} ${f(syB[1])} ${f(sx[0])} ${f(syB[0])} Z`,
    );
  }
  return segs;
};

// gainRibbonPath renders a node's increase ribbon: the gained wine runs
// left off the top of its left edge, then rises and fades out — the loss
// drain rotated 180 degrees at the topleft of the node. Derived from the
// node's live position, so it follows the node while it is being dragged.
const gainRibbonPath = (n: LNode): string => {
  const f = (v: number) => v.toFixed(1);
  const x1 = n.x;
  const yTop = n.gainY0;
  const yBot = n.gainY1; // yTop + gainW: the slot on the node's left edge
  const w = n.gainW;
  const run = Math.max(20, w); // how far the tail hangs out to the left
  let top = yTop - Math.max(40, Math.min(w * 7, 140));
  const minTop = topPad + 8;
  if (top < minTop) {
    top = Math.min(yTop - 8, minTop);
  }
  const xL = x1 - run - w; // left side of the rising tail
  // each edge is one cubic bezier with its control points at the span's
  // midpoint, so the bend is a wide, gentle S across the whole run and
  // rise (the band keeps its width w), not a tight corner
  return (
    `M${f(x1)} ${f(yBot)}` +
    ` C${f(x1 - (run + w) / 2)} ${f(yBot)} ${f(xL)} ${f(
      yTop - (yTop - top - w) / 2,
    )} ${f(xL)} ${f(top)}` +
    ` L${f(x1 - run)} ${f(top)}` +
    ` C${f(x1 - run)} ${f(yTop - (yTop - top) / 2)} ${f(
      x1 - run / 2,
    )} ${f(yTop)} ${f(x1)} ${f(yTop)} Z`
  );
};

// lossDrainPath renders a node's loss drain: the lost wine runs right off the
// bottom of its right edge, then falls and fades out. Derived from the node's
// live position, so it follows the node while it is being dragged.
const lossDrainPath = (n: LNode, height: number): string => {
  const f = (v: number) => v.toFixed(1);
  const x0 = n.x + n.w;
  const yTop = n.lossY0;
  const yBot = n.lossY1; // yTop + lossW: the slot on the node's right edge
  const w = n.lossW;
  const run = Math.max(20, w); // how far the tail hangs out to the right
  let bottom = yBot + Math.max(40, Math.min(w * 7, 140));
  const maxBottom = height - bottomPad - 8;
  if (bottom > maxBottom) {
    bottom = Math.max(yBot + 8, maxBottom);
  }
  const xR = x0 + run + w; // right side of the falling tail
  // each edge is one cubic bezier with its control points at the span's
  // midpoint, so the bend is a wide, gentle S across the whole run and fall
  // (the band keeps its width w), not a tight corner
  return (
    `M${f(x0)} ${f(yTop)}` +
    ` C${f(x0 + (run + w) / 2)} ${f(yTop)} ${f(xR)} ${f(
      yTop + (bottom - yTop) / 2,
    )} ${f(xR)} ${f(bottom)}` +
    ` L${f(x0 + run)} ${f(bottom)}` +
    ` C${f(x0 + run)} ${f(bottom - (bottom - yBot) / 2)} ${f(
      x0 + run / 2,
    )} ${f(yBot)} ${f(x0)} ${f(yBot)} Z`
  );
};

export const layoutSankey = (
  nodes: SankeyNode[],
  links: SankeyLink[],
): SankeyLayout => {
  if (nodes.length === 0) {
    return { width: 1100, height: 420 + bottomRoom, nodes: [], links: [] };
  }

  // deterministic node order: the layout passes keep it
  const sortedNodes = [...nodes].sort(
    (a, b) => a.layer - b.layer || a.id - b.id,
  );
  const nodeByID = new Map<number, LNode>();
  const lnodes: LNode[] = sortedNodes.map((node) => {
    const ln: LNode = {
      node,
      x: 0,
      y: 0,
      w: nodeW,
      h: minH,
      labelY: 0,
      in: 0,
      out: 0,
      lossFlow: 0,
      lossW: 0,
      lossY0: 0,
      lossY1: 0,
      gainFlow: 0,
      gainW: 0,
      gainY0: 0,
      gainY1: 0,
    };
    nodeByID.set(node.id, ln);
    return ln;
  });
  const llinks: LLink[] = links
    .map((l) => ({
      s: nodeByID.get(l.source)!,
      t: nodeByID.get(l.target)!,
      volume: l.volume,
      portion: l.portion,
      flow: l.portion ?? l.volume,
      n: l.n,
      minDate: l.minDate,
      maxDate: l.maxDate,
      back: !!l.back,
      width: 0,
      sy0: 0,
      sy1: 0,
      ty0: 0,
      ty1: 0,
    }))
    .filter((l) => l.s && l.t);
  for (const l of llinks) {
    l.s.out += l.flow;
    l.t.in += l.flow;
  }
  // the loss drain is extra outflow of the source node and the increase
  // ribbon extra inflow into it: each counts toward the node's height (so
  // its edge stack has room), but no other node ever sends or receives it
  for (const n of lnodes) {
    if ((n.node.loss ?? 0) > 0) {
      n.lossFlow = n.node.lossPortion ?? n.node.loss!;
      n.out += n.lossFlow;
    }
    if ((n.node.gain ?? 0) > 0) {
      n.gainFlow = n.node.gainPortion ?? n.node.gain!;
      n.in += n.gainFlow;
    }
  }
  // deterministic link order: it affects the draw order of overlapping
  // ribbons and every accumulation
  llinks.sort(
    (a, b) =>
      a.s.node.layer - b.s.node.layer ||
      a.s.node.name.localeCompare(b.s.node.name) ||
      a.t.node.layer - b.t.node.layer ||
      a.t.node.name.localeCompare(b.t.node.name),
  );

  const cols = new Map<number, LNode[]>();
  for (const n of lnodes) {
    const col = cols.get(n.node.layer);
    if (col) {
      col.push(n);
    } else {
      cols.set(n.node.layer, [n]);
    }
  }
  const colIDs = [...cols.keys()].sort((a, b) => a - b);
  reduceCrossings(colIDs, cols, llinks);

  // canvas size from the graph's shape. The labels sit to the right of
  // every node (last column included), so the right margin has to fit the
  // widest one (labelWidth)
  const maxPerCol = Math.max(...colIDs.map((c) => cols.get(c)!.length));
  const labelW = Math.max(0, ...lnodes.map((n) => labelWidth(n.node)));
  const width = Math.max(
    1280, // the legend row needs at least this much
    50 + (colIDs.length - 1) * (nodeW + colGap) + nodeW + labelW + 24,
  );
  const heightCap = 2000;
  let height = Math.min(
    heightCap,
    Math.max(420, topPad + maxPerCol * 84 + bottomPad),
  );

  // the vertical scale of the most crowded column, so every column fits
  // (flows are the root-lot shares, not the full movement volumes)
  const baseScaleOf = (availH: number) => {
    let s = 1e9;
    for (const c of colIDs) {
      const col = cols.get(c)!;
      const sum = col.reduce((acc, n) => acc + flowOf(n), 0);
      if (sum > 0) {
        const cs = (availH - rowGap * (col.length - 1)) / sum;
        if (cs < s) {
          s = cs;
        }
      }
    }
    return s > 1e8 ? 1 : s; // no flows at all: minimum heights only
  };

  const xOf = (c: number) => 50 + (c - colIDs[0]) * (nodeW + colGap);
  // one layout attempt: optionally flow-relax, un-align perfectly
  // horizontal ribbon bundles, move each column out of the way of ribbons
  // that would visibly cross it, then price the result — lexicographic,
  // crossings first (a shallow graze is far worse than a bundle),
  // horizontal bundles second
  const layOut = (relax: boolean, scale: number, bottom: number): number => {
    if (relax) {
      relaxColumns(lnodes, colIDs, cols, llinks, scale, topPad, bottom);
    }
    avoidFlatBundles(lnodes, colIDs, cols, llinks, scale, topPad, bottom);
    placeColumns(lnodes, colIDs, cols, llinks, scale, topPad, bottom);
    return 10000 * countCrossings(colIDs, cols, llinks) + countFlatBundles(lnodes, llinks);
  };
  // node heights and a centered start (depend only on the scale and the
  // canvas height)
  const initialPlacement = (scale: number, availH: number) => {
    for (const c of colIDs) {
      const col = cols.get(c)!;
      let total = 0;
      for (const n of col) {
        n.w = nodeW;
        n.h = minH;
        if (scale * flowOf(n) > n.h) {
          n.h = scale * flowOf(n);
        }
        total += n.h;
      }
      total += rowGap * (col.length - 1);
      if (total > availH) {
        // minimum heights overflowed; shrink to fit
        for (const n of col) {
          n.h *= availH / total;
        }
        total = availH;
      }
      let y = topPad + (availH - total) / 2;
      for (const n of col) {
        n.x = xOf(c);
        n.y = y;
        y += n.h + rowGap;
      }
    }
  };
  // a placement snapshot (positions and heights — together they determine
  // every ribbon's band)
  const snapOf = () => lnodes.map((n) => ({ y: n.y, h: n.h }));

  // lay the columns out: the scale shrinks in 80% steps as long as the
  // placement still has ribbons crossing unrelated nodes — or a bundle of
  // perfectly horizontal ribbons at a node's side — and when the crossing
  // ribbons simply do not fit at this canvas height, the canvas itself
  // grows (up to its cap). The cleanest layout found is kept: the score
  // is lexicographic, crossings first (a shallow graze is far worse than
  // a bundle), horizontal bundles second.
  let bestScore = Infinity;
  let bestSnap: { y: number; h: number }[] | null = null;
  let bestScale = 1;
  for (let attempt = 0; attempt < 4; attempt++) {
    const availH = height - topPad - bottomPad;
    const baseScale = baseScaleOf(availH);
    let attemptScore = Infinity;
    let attemptSnap: { y: number; h: number }[] | null = null;
    let attemptScale = 1;
    for (let factor = 1.0; factor > 0.2; factor *= 0.8) {
      const scale = baseScale * factor;
      initialPlacement(scale, availH);
      // two deterministic starts: flow-relaxed (ribbons aligned with the
      // flow) and bunched (no relaxation — tight columns keep the fan of
      // long ribbons narrow). Keep whichever ends up cleaner.
      const v1 = layOut(true, scale, height - bottomPad);
      const snap1 = snapOf();
      const v2 = layOut(false, scale, height - bottomPad);
      const snap2 = snapOf();
      const v2Wins = v2 < v1;
      const score = v2Wins ? v2 : v1;
      if (score < attemptScore) {
        attemptScore = score;
        attemptSnap = v2Wins ? snap2 : snap1;
        attemptScale = scale;
      }
      if (score === 0) {
        break; // clean; a smaller scale only makes the ribbons thinner
      }
    }
    if (attemptSnap && attemptScore < bestScore) {
      bestScore = attemptScore;
      bestSnap = attemptSnap;
      bestScale = attemptScale;
    }
    if (bestScore === 0 || height >= heightCap) {
      break;
    }
    height = Math.min(heightCap, height * 1.4); // more room for the crossing ribbons
  }
  if (bestSnap) {
    for (let i = 0; i < lnodes.length; i++) {
      lnodes[i].y = bestSnap[i].y;
      lnodes[i].h = bestSnap[i].h;
    }
  }
  assignAnchors(lnodes, llinks, bestScale); // anchors of the kept layout

  // label positions: one slot per node, in column order, so the labels of
  // neighbouring nodes never overlap (labels may drift away from short
  // nodes): 26px for name+sub, 40px when the node also carries the subsub
  // line
  for (const c of colIDs) {
    let last = 0;
    let lastTall = false;
    for (const n of cols.get(c)!) {
      let cy = n.y + n.h / 2;
      const slot = lastTall ? 40 : 26;
      if (cy < last + slot) {
        cy = last + slot;
      }
      const max = height - bottomPad - 16;
      if (cy > max) {
        cy = max;
      }
      n.labelY = cy;
      last = cy;
      lastTall = n.node.subsub != null;
    }
  }

  // the extra bottom room is added after the placement: it is free space
  // at the bottom of the svg (the footer stays anchored to the edge, the
  // drag clamp runs through to it), not part of the layout band
  return { width, height: height + bottomRoom, nodes: lnodes, links: llinks };
};

// The layout invariants, re-derived from a laid-out diagram; exported for
// the unit tests (and the invariants the placement above is driven to
// satisfy).
export const sankeyAudit = (lay: SankeyLayout) => {
  const cols = new Map<number, LNode[]>();
  for (const n of lay.nodes) {
    const col = cols.get(n.node.layer);
    if (col) {
      col.push(n);
    } else {
      cols.set(n.node.layer, [n]);
    }
  }
  const colIDs = [...cols.keys()].sort((a, b) => a - b);
  // every ribbon leaves its source's right edge pointing right and arrives
  // at its target's left edge coming from the left (the control-point
  // invariant of ribbonControl)
  let badDirections = 0;
  for (const l of lay.links) {
    const { x0, x1, c0, c1 } = ribbonControl(l);
    if (c0 <= x0 || c1 >= x1) {
      badDirections++;
    }
  }
  return {
    crossings: countCrossings(colIDs, cols, lay.links),
    bundles: countFlatBundles(lay.nodes, lay.links),
    badDirections,
  };
};

// The layout machinery the unit tests drive directly.
export const sankeyLayoutInternals = {
  assignAnchors,
  avoidFlatBundles,
};

export const Sankey = ({
  nodes,
  links,
  title,
  subtitle,
  footer,
  unit = "L",
  exportName,
  highlightID,
  rootLotName,
  onNodeClick,
}: SankeyProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const layout = useMemo(() => layoutSankey(nodes, links), [nodes, links]);
  const [tip, setTip] = useState<SankeyTip | null>(null);

  // The custom hover tooltip follows the cursor inside the wrapper, and
  // flips to the left of the cursor near the right edge.
  const showTip = (clientX: number, clientY: number, lines: TipLine[]) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    let x = clientX - rect.left + 14;
    const y = clientY - rect.top + 14;
    if (x + 280 > rect.width) x = clientX - rect.left - 294;
    setTip({ x, y, lines });
  };
  const hideTip = () => setTip(null);

  // Serialize the rendered diagram as a standalone .svg file and download
  // it (same blob + anchor pattern as the CSV exports). The in-page svg
  // scales via CSS, so the exported clone gets explicit width/height.
  const downloadSvg = () => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", String(layout.width));
    clone.setAttribute("height", String(layout.height));
    clone.removeAttribute("style");
    clone.setAttribute("font-family", "Helvetica, Arial, sans-serif");
    // The in-page tooltip is rendered as HTML, so for the exported file
    // restore SVG-native <title> tooltips from the data-tip attributes.
    clone.querySelectorAll("[data-tip]").forEach((el) => {
      const t = document.createElementNS("http://www.w3.org/2000/svg", "title");
      t.textContent = el.getAttribute("data-tip") ?? "";
      el.insertBefore(t, el.firstChild);
    });
    const blob = new Blob(
      [
        '<?xml version="1.0" encoding="UTF-8"?>\n',
        new XMLSerializer().serializeToString(clone),
      ],
      { type: "image/svg+xml;charset=utf-8" },
    );
    const file = window.URL.createObjectURL(blob);
    const name = (exportName || "sankey").replace(/[^\w.-]+/g, "-");
    const a = document.createElement("a");
    a.href = file;
    a.download = `${name}.svg`;
    document.body.appendChild(a); // must be in the DOM -> otherwise it won't work in firefox
    a.click();
    a.remove();
  };

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }
    const canvas = d3.select(svgRef.current);
    canvas.selectAll("*").remove();

    const { height, nodes: lnodes, links: llinks } = layout;

    const fmtDate = (s?: string) =>
      s ? new Date(s).toISOString().slice(0, 10) : "-";
    const fmtVol = (v: number, u: string) => `${Math.round(v)} ${u || "L"}`;

    // fades for the loss drains and the increase ribbons: opaque at the
    // node's edge, transparent at the tail (the gain's tail hangs above
    // the node, so its gradient runs the other way) — per-svg ids, so
    // exports and re-renders don't collide
    const fade = (id: string, topOpacity: number) => {
      const grad = canvas
        .append("defs")
        .append("linearGradient")
        .attr("id", id)
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("y2", "1");
      grad
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#7d3140")
        .attr("stop-opacity", topOpacity);
      grad
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#7d3140")
        .attr("stop-opacity", 1 - topOpacity);
      return `url(#${id})`;
    };
    const lossFade = fade(`loss-fade-${++lossFadeSeq}`, 1);
    const gainFade = fade(`gain-fade-${++lossFadeSeq}`, 0);

    // Tooltip content: ribbonTip is the formatted HTML lines, ribbonTipText
    // the flat one-liner stored in data-tip for the SVG export. Volumes are
    // the root-lot shares when the graph carries them: "50 L of 500 L".
    const moveCount = (l: LLink) =>
      ` over ${l.n} movement${l.n === 1 ? "" : "s"}`;
    const ribbonTip = (l: LLink): TipLine[] => [
      { text: `${l.s.node.name} \u2192 ${l.t.node.name}`, strong: true },
      {
        text:
          l.portion != null
            ? l.portion === l.volume
              ? `${fmtVol(l.portion, unit)}${moveCount(l)}`
              : `${fmtVol(l.portion, unit)} of ${fmtVol(l.volume, unit)}${moveCount(l)}`
            : `${fmtVol(l.volume, unit)}${moveCount(l)}`,
      },
      ...(l.minDate || l.maxDate
        ? [
            {
              text: `${fmtDate(l.minDate)} .. ${fmtDate(l.maxDate)}`,
              muted: true,
            },
          ]
        : []),
    ];
    const ribbonTipText = (l: LLink) => {
      const dates =
        l.minDate || l.maxDate
          ? `, ${fmtDate(l.minDate)} .. ${fmtDate(l.maxDate)}`
          : "";
      return `${ribbonTip(l)[0].text}: ${ribbonTip(l)[1].text}${dates}`;
    };
    // the intake's fruit detail: one line per parcel of its extraction
    // workorder (grower, block, docket, weight)
    const parcelTipLines = (n: LNode): TipLine[] => {
      const ps = n.node.parcels ?? [];
      if (ps.length === 0) {
        return [];
      }
      return ps.map((p) => ({
        text: [
          p.growerName,
          p.blockName,
          p.docket,
          p.weight ? `${p.weight} ${p.weightUnit || "t"}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
        muted: true,
      }));
    };
    const nodeTip = (n: LNode): TipLine[] => [
      { text: n.node.name, strong: true },
      ...(n.node.sub ? [{ text: n.node.sub, muted: true }] : []),
      ...(n.node.subsub ? [{ text: n.node.subsub, muted: true }] : []),
      ...parcelTipLines(n),
      ...(n.node.dim
        ? [
            {
              text: "dimmed: small share of the root lot; the walk stops here",
              muted: true,
            },
          ]
        : []),
      ...(n.node.portion != null && n.node.total != null
        ? n.node.portion === n.node.total
          ? [{ text: fmtVol(n.node.portion, unit) }]
          : [
              {
                text: `${fmtVol(n.node.portion, unit)} of ${fmtVol(
                  n.node.total,
                  unit,
                )} pertains to ${rootLotName ?? "this lot"}`,
              },
            ]
        : []),
    ];
    // the loss drain's tooltip: the lost wine that pertains to the root
    // lot, over the batch's full loss (the ribbon is sized by the same
    // share)
    const lossVolTip = (n: LNode) => {
      const loss = n.node.loss ?? 0;
      const p = n.node.lossPortion;
      return p != null && p !== loss
        ? `${fmtVol(p, unit)} of ${fmtVol(loss, unit)} lost`
        : `${fmtVol(p ?? loss, unit)} lost`;
    };
    const lossTip = (n: LNode): TipLine[] => [
      { text: n.node.name, strong: true },
      { text: lossVolTip(n) },
    ];
    const lossTipText = (n: LNode) => `${n.node.name}: ${lossVolTip(n)}`;
    // the increase ribbon's tooltip: the gained wine that pertains to the
    // root lot, over the batch's full gain (the ribbon is sized by the
    // same share)
    const gainVolTip = (n: LNode) => {
      const gain = n.node.gain ?? 0;
      const p = n.node.gainPortion;
      return p != null && p !== gain
        ? `${fmtVol(p, unit)} of ${fmtVol(gain, unit)} gained`
        : `${fmtVol(p ?? gain, unit)} gained`;
    };
    const gainTip = (n: LNode): TipLine[] => [
      { text: n.node.name, strong: true },
      { text: gainVolTip(n) },
    ];
    const gainTipText = (n: LNode) => `${n.node.name}: ${gainVolTip(n)}`;

    canvas
      .append("rect")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("fill", "#ffffff");

    canvas
      .append("text")
      .attr("x", 24)
      .attr("y", 34)
      .attr("font-size", 17)
      .attr("font-weight", "bold")
      .attr("fill", "#1c1917")
      .text(title);

    if (subtitle) {
      canvas
        .append("text")
        .attr("x", 24)
        .attr("y", 54)
        .attr("font-size", 12)
        .attr("fill", "#57534e")
        .text(subtitle);
    }

    // legend
    const legendY = 74;
    const legend = [
      {
        x: 24,
        fill: "#7d3140",
        opacity: 1,
        txt: "batch, fermentation started (StartFerment)",
      },
      {
        x: 330,
        fill: "#8b93a1",
        opacity: 1,
        txt: "batch, no StartFerment in stored data",
      },
      {
        x: 640,
        fill: "#5b7c9d",
        opacity: 1,
        txt: "vessel / intake / packaging (no vintrace batch)",
      },
      {
        x: 940,
        fill: "#7d3140",
        opacity: 0.3,
        txt: "ribbon = lot's volume",
      },
      {
        x: 1150,
        fill: lossFade,
        opacity: 1,
        txt: "loss, fading out",
      },
      {
        x: 1330,
        fill: gainFade,
        opacity: 1,
        txt: "increase, fading in",
      },
      {
        x: 1520,
        fill: "#8b93a1",
        opacity: dimNodeOpacity,
        txt: "dimmed: small share, walk stops",
      },
    ];
    for (const item of legend) {
      canvas
        .append("rect")
        .attr("x", item.x)
        .attr("y", legendY - 10)
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .attr("fill", item.fill)
        .attr("opacity", item.opacity)
        .attr("stroke", "#57534e")
        .attr("stroke-width", 0.5);
      canvas
        .append("text")
        .attr("x", item.x + 17)
        .attr("y", legendY)
        .attr("font-size", 11)
        .attr("fill", "#57534e")
        .text(item.txt);
    }

    // ribbons first, so nodes and labels sit on top. A ribbon that folds
    // back over itself is one path per x-monotonic window (see
    // ribbonSegments); the windows are painted with their own opacity, so
    // where they overlap the color is painted twice and reads darker
    // instead of the fill cancelling itself out
    interface RibbonSeg {
      l: LLink;
      key: string;
      d: string;
    }
    const ribbonSegs: RibbonSeg[] = [];
    for (const l of llinks) {
      const key = `${l.s.node.id}-${l.t.node.id}`;
      for (const d of ribbonSegments(l)) {
        ribbonSegs.push({ l, key, d });
      }
    }
    // a ribbon touching a dimmed node fades with it
    const ribbonBase = (l: LLink) =>
      l.s.node.dim || l.t.node.dim ? dimRibbonOpacity : ribbonOpacity;
    const linkPaths = canvas
      .append("g")
      .selectAll("path")
      .data(ribbonSegs)
      .join("path")
      .attr("d", (s: RibbonSeg) => s.d)
      .attr("fill", "#7d3140")
      .attr("opacity", (s: RibbonSeg) => ribbonBase(s.l));
    linkPaths
      .attr("data-ribbon", (s: RibbonSeg) => s.key)
      .attr("data-tip", (s: RibbonSeg) => ribbonTipText(s.l))
      .attr("data-testid", (s: RibbonSeg) => `vintrace-ribbon-${s.key}`)
      .on("mousemove", (event: any, s: RibbonSeg) => {
        // emphasize every window of the hovered ribbon (restored on
        // mouseleave)
        canvas
          .selectAll(`[data-ribbon="${s.key}"]`)
          .attr("opacity", String(ribbonOpacityHover));
        showTip(event.clientX, event.clientY, ribbonTip(s.l));
      })
      .on("mouseleave", (_event: any, s: RibbonSeg) => {
        canvas
          .selectAll(`[data-ribbon="${s.key}"]`)
          .attr("opacity", String(ribbonBase(s.l)));
        hideTip();
      });

    // loss drains: the wine a batch lost flows out of the bottom of its
    // right edge (the loss slot of the outgoing stack), runs right like the
    // other outgoing ribbons, then curves down and fades out. Drawn under
    // the nodes, like the ribbons
    for (const n of lnodes) {
      if (n.lossFlow <= 0) {
        continue;
      }
      canvas
        .append("path")
        .attr("data-testid", `vintrace-loss-${n.node.id}`)
        .attr("data-tip", lossTipText(n))
        .attr("d", lossDrainPath(n, height))
        .attr("fill", lossFade)
        .attr("opacity", n.node.dim ? dimRibbonOpacity : ribbonOpacity)
        .on("mousemove", (event: any) => {
          // emphasize the hovered drain (restored on mouseleave)
          event.currentTarget.setAttribute(
            "opacity",
            String(ribbonOpacityHover),
          );
          showTip(event.clientX, event.clientY, lossTip(n));
        })
        .on("mouseleave", (event: any) => {
          event.currentTarget.setAttribute(
            "opacity",
            String(n.node.dim ? dimRibbonOpacity : ribbonOpacity),
          );
          hideTip();
        });
    }

    // increase ribbons: the wine a batch's net adjustments added to it
    // enters the top of its left edge (the gain slot of the incoming
    // stack), runs left like the other incoming ribbons, then curves up
    // and fades out — the loss drain rotated 180 degrees
    for (const n of lnodes) {
      if (n.gainFlow <= 0) {
        continue;
      }
      canvas
        .append("path")
        .attr("data-testid", `vintrace-gain-${n.node.id}`)
        .attr("data-tip", gainTipText(n))
        .attr("d", gainRibbonPath(n))
        .attr("fill", gainFade)
        .attr("opacity", n.node.dim ? dimRibbonOpacity : ribbonOpacity)
        .on("mousemove", (event: any) => {
          // emphasize the hovered ribbon (restored on mouseleave)
          event.currentTarget.setAttribute(
            "opacity",
            String(ribbonOpacityHover),
          );
          showTip(event.clientX, event.clientY, gainTip(n));
        })
        .on("mouseleave", (event: any) => {
          event.currentTarget.setAttribute(
            "opacity",
            String(n.node.dim ? dimRibbonOpacity : ribbonOpacity),
          );
          hideTip();
        });
    }

    // Vertical node dragging: the automatic placement is a starting point,
    // so the user can nudge a node up or down. Only the node's y changes
    // (its column is fixed); every ribbon anchor on the node's edges follows
    // it, and the attached ribbons, labels and loss drain re-render in real
    // time. The drag mutates the laid-out data in place (the same objects the
    // memo holds), so a manual position survives re-renders until the data
    // itself changes.
    let dragStyle: string | null = null;
    let dragging = false;
    let lastDragEnd = 0;
    const moveNode = (n: LNode, g: any, y: number) => {
      const dy = shiftNodeVertical(n, llinks, y, topPad, height - bottomPad);
      if (dy === 0) {
        return;
      }
      // re-render the node, its loss drain, and every attached ribbon
      g.select("rect").attr("y", n.y.toFixed(1));
      // the label lines' offsets from labelY (name, sub, subsub)
      const lineDY = [-1, 12, 24];
      g.selectAll("text").attr(
        "y",
        (_d: unknown, i: number) => (n.labelY + (lineDY[i] ?? 12)).toFixed(1),
      );
      if (n.lossFlow > 0) {
        canvas
          .select(`[data-testid="vintrace-loss-${n.node.id}"]`)
          .attr("d", lossDrainPath(n, height));
      }
      if (n.gainFlow > 0) {
        canvas
          .select(`[data-testid="vintrace-gain-${n.node.id}"]`)
          .attr("d", gainRibbonPath(n));
      }
      for (const l of llinks) {
        if (l.s === n || l.t === n) {
          const segs = ribbonSegments(l);
          canvas
            .selectAll(`[data-ribbon="${l.s.node.id}-${l.t.node.id}"]`)
            .attr("d", (_s: unknown, i: number) => segs[i]);
        }
      }
    };
    // ignore the click that ends a drag, not a real click
    const handleNodeClick = (event: any, n: LNode) => {
      if (Date.now() - lastDragEnd < 250) {
        return;
      }
      if (n.node.lotID != null && onNodeClick) {
        event.stopPropagation();
        onNodeClick(n.node);
      }
    };
    // vertical drag: nudge the node up/down; its ribbons (and loss drain)
    // follow in real time. Declared outside the node loop so the handlers
    // don't capture the loop-scoped mutable drag state (no-loop-func)
    const attachDrag = (n: LNode, g: any) => {
      g.call(
        d3.drag()
          .container(() => svgRef.current as SVGSVGElement)
          .subject(() => ({ x: n.x, y: n.y }))
          .on("start", () => {
            dragging = false;
          })
          .on("drag", (event: any) => {
            if (!dragging) {
              dragging = true;
              dragStyle = g.attr("style");
              g.attr("style", "cursor: grabbing");
              g.raise();
            }
            moveNode(n, g, event.y);
          })
          .on("end", () => {
            if (dragging) {
              dragging = false;
              g.attr("style", dragStyle);
              lastDragEnd = Date.now();
            }
          }),
      );
    };

    for (const n of lnodes) {
      let fill = "#8b93a1";
      let stroke = "#5f6673";
      if (n.node.kind !== "batch") {
        fill = "#5b7c9d";
        stroke = "#3d5a7a";
      } else if (n.node.ferm) {
        fill = "#7d3140";
        stroke = "#521f28";
      }

      const node = canvas.append("g");
      node
        .attr("data-testid", `vintrace-node-${n.node.id}`)
        .attr(
          "data-tip",
          n.node.sub ? `${n.node.name} (${nodeSubLabel(n.node)})` : n.node.name,
        )
        .attr("opacity", n.node.dim ? dimNodeOpacity : 1)
        .attr("style", n.node.lotID != null ? "cursor: pointer" : null)
        .on("mousemove", (event: any) =>
          showTip(event.clientX, event.clientY, nodeTip(n)),
        )
        .on("mouseleave", hideTip)
        .on("click", (event: any) => handleNodeClick(event, n));
      node
        .append("rect")
        .attr("x", n.x.toFixed(1))
        .attr("y", n.y.toFixed(1))
        .attr("width", n.w.toFixed(1))
        .attr("height", n.h.toFixed(1))
        .attr("rx", 3)
        .attr("fill", fill)
        .attr("stroke", stroke);
      // labels always on the right of the node (last column included)
      const lx = n.x + n.w + 7;
      node
        .append("text")
        .attr("x", lx.toFixed(1))
        .attr("y", (n.labelY - 1).toFixed(1))
        .attr("font-size", 13)
        .attr("font-weight", n.node.id === highlightID ? "bold" : "normal")
        .attr("fill", "#292524")
        .attr("text-anchor", "start")
        .text(n.node.name);
      node
        .append("text")
        .attr("x", lx.toFixed(1))
        .attr("y", (n.labelY + 12).toFixed(1))
        .attr("font-size", 10)
        .attr("fill", "#78716c")
        .attr("text-anchor", "start")
        .text(nodeSubLabel(n.node));
      if (n.node.subsub) {
        node
          .append("text")
          .attr("x", lx.toFixed(1))
          .attr("y", (n.labelY + 24).toFixed(1))
          .attr("font-size", 10)
          .attr("fill", "#a8a29e")
          .attr("text-anchor", "start")
          .text(n.node.subsub);
      }
      attachDrag(n, node);
    }

    if (footer) {
      canvas
        .append("text")
        .attr("x", 24)
        .attr("y", height - 24)
        .attr("font-size", 11)
        .attr("fill", "#78716c")
        .text(footer);
    }
  }, [layout, title, subtitle, footer, unit, highlightID, rootLotName, onNodeClick]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {exportName !== undefined && (
        <Button
          size="small"
          variant="outlined"
          onClick={downloadSvg}
          style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          Export
        </Button>
      )}
      <div style={{ overflowX: "auto" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          style={{
            // the full geometry lives in the viewBox; the width/height cap the
            // display size so the whole diagram scales onto one page
            width: "100%",
            minWidth: 900,
            maxWidth: 1700,
            height: "auto",
            display: "block",
            fontFamily: "Helvetica, Arial, sans-serif",
          }}
        />
      </div>
      {tip && (
        <div
          style={{
            position: "absolute",
            left: tip.x,
            top: tip.y,
            zIndex: 2,
            pointerEvents: "none",
            background: "#1c1917",
            color: "#fafaf9",
            borderRadius: 4,
            padding: "6px 10px",
            fontSize: 12,
            lineHeight: 1.45,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
          }}
        >
          {tip.lines.map((l, i) => (
            <div
              key={i}
              style={{
                fontWeight: l.strong ? 600 : 400,
                color: l.muted ? "#a8a29e" : "#fafaf9",
              }}
            >
              {l.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
